import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  MessageSquarePlus,
  Sparkles,
  Underline,
} from "lucide-react";
import {
  selectionToolbarBottomStyle,
  useKeyboardInset,
} from "../hooks/useKeyboardInset";

export type SelectionToolbarState = {
  visible: boolean;
  quote: string;
};

const HIDDEN: SelectionToolbarState = {
  visible: false,
  quote: "",
};

const FONTS = [
  { value: "Vazirmatn, Tahoma, sans-serif", label: "وزیرمتن" },
  { value: "Tahoma, sans-serif", label: "Tahoma" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Courier New, monospace", label: "Courier" },
];

type Props = {
  state: SelectionToolbarState;
  /** full = DOCX formatting; comment = PDF/selection comment + AI only */
  mode?: "full" | "comment";
  onFontFamily: (font: string) => void;
  onFontColor: (color: string) => void;
  onTextAlign: (align: "right" | "left" | "center" | "justify") => void;
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onComment: () => void;
  /** Send current selection to AI assistant */
  onSendToAssistant?: () => void;
};

/** One-line formatting bar above the IME / system nav; horizontal scroll. */
export function FloatingSelectionToolbar({
  state,
  mode = "full",
  onFontFamily,
  onFontColor,
  onTextAlign,
  onBold,
  onItalic,
  onUnderline,
  onComment,
  onSendToAssistant,
}: Props) {
  const [mounted, setMounted] = useState(false);
  const keyboardInset = useKeyboardInset();
  useEffect(() => setMounted(true), []);

  if (!mounted || !state.visible) return null;

  const dock = selectionToolbarBottomStyle(keyboardInset);
  const commentOnly = mode === "comment";

  return createPortal(
    <div
      data-floating-selection-toolbar
      className="selection-bottom-bar"
      role="toolbar"
      aria-label="ابزار قالب‌بندی"
      style={{
        bottom: dock.bottom,
        paddingBottom: dock.paddingBottom,
      }}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement | null;
        if (target?.closest("select, input")) return;
        e.preventDefault();
      }}
      onTouchStart={(e) => {
        const target = e.target as HTMLElement | null;
        if (target?.closest("select, input")) return;
        e.preventDefault();
      }}
    >
      <div className="selection-bottom-bar-inner selection-bottom-bar-scroll">
        {onSendToAssistant && (
          <button
            type="button"
            className="sbb-ai icon-only"
            aria-label="ارسال به دستیار"
            title="ارسال به دستیار"
            onClick={onSendToAssistant}
          >
            <Sparkles size={16} aria-hidden />
          </button>
        )}
        <button
          type="button"
          className="sbb-comment icon-only"
          aria-label="افزودن کامنت"
          title="افزودن کامنت"
          onClick={onComment}
        >
          <MessageSquarePlus size={17} />
        </button>
        {!commentOnly && (
          <>
            <span className="ft-sep" />
            {(
              [
                ["right", AlignRight, "راست‌چین"],
                ["center", AlignCenter, "وسط"],
                ["left", AlignLeft, "چپ‌چین"],
                ["justify", AlignJustify, "کشیده"],
              ] as const
            ).map(([align, Icon, label]) => (
              <MiniBtn
                key={align}
                label={label}
                onClick={() => onTextAlign(align)}
              >
                <Icon size={17} />
              </MiniBtn>
            ))}
            <span className="ft-sep" />
            <MiniBtn label="پررنگ" onClick={onBold}>
              <Bold size={17} />
            </MiniBtn>
            <MiniBtn label="ایتالیک" onClick={onItalic}>
              <Italic size={17} />
            </MiniBtn>
            <MiniBtn label="زیرخط" onClick={onUnderline}>
              <Underline size={17} />
            </MiniBtn>
            <span className="ft-sep" />
            <select
              aria-label="فونت"
              defaultValue={FONTS[0]!.value}
              onChange={(e) => onFontFamily(e.target.value)}
            >
              {FONTS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <label className="sbb-color" title="رنگ متن">
              <span className="sr-only">رنگ متن</span>
              <input
                type="color"
                aria-label="رنگ متن"
                defaultValue="#1B3A4B"
                onChange={(e) => onFontColor(e.target.value)}
              />
            </label>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function MiniBtn({
  children,
  label,
  onClick,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="ft-btn"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function readDomSelection(root: HTMLElement): SelectionToolbarState | null {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;

  const anchorEl =
    range.commonAncestorContainer instanceof Element
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  if (
    anchorEl?.closest(
      "button, [data-floating-selection-toolbar], .comment-focus-sheet, .add-comment-sheet"
    )
  ) {
    return null;
  }

  if (anchorEl?.closest(".nr-comment-range, .nr-comment-anchor")) {
    return null;
  }

  const quote = sel.toString();
  if (!quote.trim()) return null;

  return { visible: true, quote };
}

/** Track text selection inside a contentEditable / document root. */
export function useSelectionToolbar(
  containerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean
) {
  const [state, setState] = useState<SelectionToolbarState>(HIDDEN);
  const suppressUntilRef = useRef(0);

  const hide = useCallback(() => {
    setState((s) => (s.visible ? HIDDEN : s));
  }, []);

  const suppressBriefly = useCallback(() => {
    suppressUntilRef.current = Date.now() + 400;
    hide();
  }, [hide]);

  const update = useCallback(() => {
    if (!enabled) {
      hide();
      return;
    }
    if (Date.now() < suppressUntilRef.current) {
      return;
    }
    const root = containerRef.current;
    if (!root) {
      hide();
      return;
    }

    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active.closest(
        "[data-floating-selection-toolbar], .add-comment-sheet, .comment-focus-sheet"
      )
    ) {
      return;
    }

    const next = readDomSelection(root);
    if (next) {
      setState(next);
      return;
    }
    hide();
  }, [containerRef, enabled, hide]);

  useEffect(() => {
    if (!enabled) {
      hide();
      return;
    }

    let root: HTMLElement | null = null;
    let raf = 0;

    const onPointerUp = () => {
      requestAnimationFrame(() => update());
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (
        e.shiftKey ||
        e.key.startsWith("Arrow") ||
        e.key === "Home" ||
        e.key === "End"
      ) {
        requestAnimationFrame(() => update());
      }
    };

    const onSelectionChange = () => {
      requestAnimationFrame(() => update());
    };

    const onScrollOrResize = () => {
      update();
    };

    const bind = (el: HTMLElement) => {
      el.addEventListener("mouseup", onPointerUp);
      el.addEventListener("touchend", onPointerUp);
      el.addEventListener("keyup", onKeyUp);
      el.addEventListener("scroll", onScrollOrResize, true);
      document.addEventListener("selectionchange", onSelectionChange);
      window.addEventListener("resize", onScrollOrResize);
    };

    const unbind = (el: HTMLElement) => {
      el.removeEventListener("mouseup", onPointerUp);
      el.removeEventListener("touchend", onPointerUp);
      el.removeEventListener("keyup", onKeyUp);
      el.removeEventListener("scroll", onScrollOrResize, true);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("resize", onScrollOrResize);
    };

    let retries = 0;
    const tryBind = () => {
      const el = containerRef.current;
      if (!el) {
        retries += 1;
        if (retries < 120) raf = requestAnimationFrame(tryBind);
        return;
      }
      if (root === el) return;
      if (root) unbind(root);
      root = el;
      bind(el);
    };

    tryBind();

    return () => {
      cancelAnimationFrame(raf);
      if (root) unbind(root);
    };
  }, [containerRef, enabled, hide, update]);

  return { state, setState, hide, suppressBriefly };
}
